import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type { ItemDTO } from '@central/types';
import { api } from '@/lib/api';

export type CourseFormato =
  | 'curso-online' | 'formacao' | 'mentoria-grupo' | 'imersao-presencial' | 'aula-avulsa';
export type PublicoAlvo = 'mentoradas' | 'publico-geral' | 'funcionarias' | 'convidadas';
export type LessonStatus = 'planejada' | 'roteirizada' | 'gravada' | 'editada' | 'publicada';

export interface CourseMetadataDTO {
  item_id: number;
  formato: CourseFormato | null;
  publico_alvo: PublicoAlvo | null;
  data_lancamento: string | null;
  preco_centavos: number | null;
  url_venda: string | null;
  numero_aulas: number;
  duracao_total_min: number | null;
  proxima_aula_em: string | null;
  taxa_conclusao: number | null;
  atualizado_em: string;
}

export interface LessonMetadataDTO {
  item_id: number;
  course_item_id: number | null;
  ordem: number;
  duracao_min: number | null;
  status_producao: LessonStatus;
  ao_vivo_em: string | null;
  aula_url: string | null;
  atualizado_em: string;
}

export interface CourseDTO {
  item: ItemDTO;
  metadata: CourseMetadataDTO | null;
  numero_aulas: number;
}

export interface CourseWithLessonsDTO extends CourseDTO {
  aulas: Array<{ item: ItemDTO; metadata: LessonMetadataDTO }>;
}

export interface BacklogEntry {
  status: LessonStatus;
  count: number;
  aulas: Array<{ item_id: number; nome: string; course_nome: string | null; ordem: number }>;
}

export interface NextLiveLesson {
  course_item_id: number;
  course_nome: string;
  lesson_item_id: number | null;
  lesson_nome: string | null;
  ao_vivo_em: string;
}

export function useCourses(filters: { formato?: CourseFormato; status?: string } = {}): UseQueryResult<{
  courses: CourseDTO[];
  total: number;
}> {
  const params = new URLSearchParams();
  if (filters.formato) params.set('formato', filters.formato);
  if (filters.status) params.set('status', filters.status);
  const qstr = params.toString();
  return useQuery({
    queryKey: ['courses', filters],
    queryFn: () => api<{ courses: CourseDTO[]; total: number }>(`/api/courses${qstr ? '?' + qstr : ''}`),
  });
}

export function useCourseDetails(id: number | null): UseQueryResult<CourseWithLessonsDTO> {
  return useQuery({
    queryKey: ['courses', 'detail', id],
    queryFn: () => api<CourseWithLessonsDTO>(`/api/courses/${id}`),
    enabled: id !== null && id > 0,
  });
}

export function useBacklog(): UseQueryResult<{ backlog: BacklogEntry[] }> {
  return useQuery({
    queryKey: ['courses', 'backlog'],
    queryFn: () => api<{ backlog: BacklogEntry[] }>('/api/courses/backlog'),
    refetchInterval: 60_000,
  });
}

export function useNextLive(): UseQueryResult<{ next: NextLiveLesson[] }> {
  return useQuery({
    queryKey: ['courses', 'next-live'],
    queryFn: () => api<{ next: NextLiveLesson[] }>('/api/courses/next-live'),
    refetchInterval: 60_000,
  });
}

export function useCourseMaterials(id: number | null): UseQueryResult<{ materials: ItemDTO[] }> {
  return useQuery({
    queryKey: ['courses', 'materials', id],
    queryFn: () => api<{ materials: ItemDTO[] }>(`/api/courses/${id}/materials`),
    enabled: id !== null && id > 0,
  });
}

export function useUpdateCourseMetadata(
  id: number,
): ReturnType<typeof useMutation<CourseMetadataDTO, Error, Partial<CourseMetadataDTO>>> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      api<CourseMetadataDTO>(`/api/courses/${id}/metadata`, { method: 'PATCH', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['courses'] }),
  });
}

export function useUpdateLessonStatus(): ReturnType<
  typeof useMutation<LessonMetadataDTO, Error, { id: number; status_producao: LessonStatus }>
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status_producao }) =>
      api<LessonMetadataDTO>(`/api/courses/lessons/${id}`, {
        method: 'PATCH',
        body: { status_producao },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['courses'] }),
  });
}
